import {useCallback, useState, type FormEvent} from 'react';
import {Banner, Button, FormLayout, Link, Text, TextField} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';

import {AuthLayout} from './auth-layout';

/**
 * Two pages for the password-reset flow. Both post to Better Auth's own endpoints via
 * fetch so JSON errors surface inline, exactly like the sign-in page.
 *
 * Note the deliberate non-disclosure on the request page: whatever the API returns, we
 * show the same "if that address exists, a link is on its way" message. Saying we
 * *didn't* find the address would turn this into an account-enumeration oracle.
 */

export interface ForgotProps extends PagePropsBase {
  resetPageUrl: string;
}

export function ForgotPage({resetPageUrl}: ForgotProps) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setLoading(true);
      try {
        await fetch('/api/auth/request-password-reset', {
          method: 'POST',
          headers: {'content-type': 'application/json'},
          body: JSON.stringify({email: email.trim(), redirectTo: resetPageUrl}),
        });
      } catch {
        // Same outcome regardless: never reveal whether the address is registered.
      }
      setDone(true);
    },
    [email, resetPageUrl],
  );

  return (
    <AuthLayout title="Reset password">
      <Text as="p" tone="subdued">
        We&rsquo;ll email you a link to set a new one.
      </Text>

      {done ? (
        <Banner tone="success">
          <p>If that address has an account, a reset link is on its way. It expires in an hour.</p>
        </Banner>
      ) : (
        <form onSubmit={onSubmit}>
          <FormLayout>
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={setEmail}
              requiredIndicator
              autoFocus
            />
            <Button submit variant="primary" fullWidth loading={loading}>
              Email me a link
            </Button>
          </FormLayout>
        </form>
      )}

      <Text as="p" tone="subdued">
        <Link url="/sign-in">Back to sign in</Link>
      </Text>
    </AuthLayout>
  );
}

export interface ResetProps extends PagePropsBase {
  signInUrl: string;
  /** better-auth redirects here with ?error=INVALID_TOKEN when the link is stale;
   *  the token itself arrives as ?token=. Read server-side so SSR and hydration
   *  agree (location.search would differ between worker and browser). */
  token: string | null;
  linkError: string | null;
}

export function ResetPage({signInUrl, token, linkError}: ResetProps) {
  const invalid = !token || !!linkError;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | undefined>(
    invalid ? 'This reset link is invalid or has expired. Request a new one.' : undefined,
  );
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(undefined);
      if (password !== confirm) {
        setError('Those passwords do not match.');
        return;
      }
      setLoading(true);
      try {
        const res = await fetch('/api/auth/reset-password', {
          method: 'POST',
          headers: {'content-type': 'application/json'},
          body: JSON.stringify({newPassword: password, token}),
        });
        if (res.ok) {
          window.location.href = signInUrl;
          return;
        }
        const data = (await res.json().catch(() => ({}))) as {message?: string};
        setError(data.message || 'Could not reset the password. The link may have expired.');
      } catch {
        setError('Could not reach the server. Try again.');
      }
      setLoading(false);
    },
    [password, confirm, token, signInUrl],
  );

  return (
    <AuthLayout title="Set a new password">
      <Text as="p" tone="subdued">
        At least 12 characters.
      </Text>

      {error ? (
        <Banner tone="critical" onDismiss={invalid ? undefined : () => setError(undefined)}>
          <p>{error}</p>
        </Banner>
      ) : null}

      {invalid ? (
        <Text as="p" tone="subdued">
          <Link url="/forgot">Request a new reset link</Link>
        </Text>
      ) : (
        <form onSubmit={onSubmit}>
          <FormLayout>
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              minLength={12}
              requiredIndicator
              autoFocus
            />
            <TextField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
              minLength={12}
              requiredIndicator
            />
            <Button submit variant="primary" fullWidth loading={loading}>
              Set new password
            </Button>
          </FormLayout>
        </form>
      )}
    </AuthLayout>
  );
}
